import { z } from "zod";

import {
  type AssociationRecord,
  AssociationType,
} from "@/shared/domain/association";
import {
  CanonicalDataType,
  type MessagePathPart,
  MessagePathPartType,
  RepresentationStorageFormat,
} from "@/shared/domain/topics";

// ---------------------------------------------------------------------------
// Hand-written interface — used as the application-level type.
// The zod schema below validates at runtime; this interface is the canonical
// TypeScript type so that hand-written code (toTopicData, reducers, etc.)
// doesn't fight with z.looseObject index signatures.
// ---------------------------------------------------------------------------

export interface TopicData {
  topic: {
    id: string;
    name: string;
    association: AssociationRecord;
    startTime?: string | null;
    endTime?: string | null;
    messageCount?: number | null;
  };
  messagePath: {
    id: string;
    dotPath: string;
    metadata: Record<string, unknown>;
    parts: MessagePathPart[];
  };
  representation: {
    association: AssociationRecord;
    id: string;
    format: RepresentationStorageFormat;
  };
}

// ---------------------------------------------------------------------------
// Zod schema — runtime validation for persisted workspace state.
// Cast to z.ZodType<TopicData> because z.looseObject / .passthrough()
// infers index signatures that are incompatible with the hand-written
// interface above.
// ---------------------------------------------------------------------------

const messagePathAttrSchema = z.strictObject({
  type: z.literal(MessagePathPartType.Attr),
  attribute: z.string(),
  dataType: z.enum(CanonicalDataType),
});

const messagePathSliceSchema = z.strictObject({
  type: z.literal(MessagePathPartType.Slice),
  start: z.number(),
  end: z.number().nullable().optional(),
});

const messagePathPartSchema = z.union([
  messagePathAttrSchema,
  messagePathSliceSchema,
]);

const associationSchema: z.ZodType<AssociationRecord> = z.object({
  association_id: z.string(),
  association_type: z.enum([
    AssociationType.Dataset,
    AssociationType.File,
  ] as const),
  association_version: z.number().nullable().optional(),
});

const representationSchema = z.object({
  association: associationSchema,
  id: z.string(),
  format: z.enum(RepresentationStorageFormat),
});

export const topicDataSchema = z.object({
  topic: z
    .object({
      id: z.string(),
      name: z.string(),
      association: associationSchema,
      startTime: z.string().nullable().optional(),
      endTime: z.string().nullable().optional(),
      messageCount: z.number().nullable().optional(),
    })
    .passthrough(),
  messagePath: z.object({
    id: z.string(),
    dotPath: z.string(),
    metadata: z.record(z.string(), z.unknown()),
    parts: z.array(messagePathPartSchema),
  }),
  representation: representationSchema,
}) as z.ZodType<TopicData>;

export interface TopicDataPathBinding {
  fileId: string;
  path: string;
}
